import type { RouteLocationResolvedGeneric } from 'vue-router'
import type { UserInfo } from '@/stores/userStore'
import { PermissionCode, RoleCode } from '@/constants/accessControl'

const hasPermission = (user: UserInfo | null, permission: string) =>
  Boolean(user?.permissions?.includes(permission))

export function getDefaultAuthorizedRoute(user: UserInfo | null): string {
  const caseCandidates: Array<[string, string]> = [
    [PermissionCode.MenuCaseHome, '/dashboard'],
    [PermissionCode.MenuIncomingTemporary, '/incoming/temporaryList'],
    [PermissionCode.MenuWebTemporary, '/webincoming/WebTemporaryList'],
    [PermissionCode.MenuWebToContact, '/webincoming/WebToContactList'],
    [PermissionCode.MenuWebConfirmed, '/webincoming/WebConfirmedList'],
    [PermissionCode.MenuCaseReady, '/case/ReadyToCaseToDoList'],
    [PermissionCode.MenuCaseAccepted, '/case/AlreadyToCaseToDoList'],
    [PermissionCode.MenuCaseReviewing, '/case/AlreadyToReviewToDoList'],
    [PermissionCode.MenuCaseReturned, '/case/ReturnedList'],
    [PermissionCode.MenuCaseCanceled, '/case/canceled'],
    [PermissionCode.MenuCaseApprovedPendingContract, '/case/ApprovedPendingContractList'],
    [PermissionCode.MenuCaseRejected, '/case/RejectedList'],
    [PermissionCode.MenuCasePendingSupplement, '/case/PendingSupplementList'],
    [PermissionCode.MenuCaseConditional, '/case/ConditionalApprovalList'],
    [PermissionCode.MenuCaseSelfCanceled, '/case/SelfCancelList'],
    [PermissionCode.MenuCaseTransfer, '/case/TransferList'],
    [PermissionCode.MenuCaseAppropriationPending, '/case/AlreadyToAppropriationToDoList'],
    [PermissionCode.MenuCaseAppropriation, '/case/AlreadyToAppropriationList'],
    [PermissionCode.MenuCaseAccounting, '/case/AlreadyAccountingList'],
    [PermissionCode.MenuCaseSupplement, '/case/SupplementList'],
    [PermissionCode.MenuCaseSupplementReturn, '/case/SupplementReturnList'],
    [PermissionCode.MenuCaseAppropriationReturn, '/case/AppropriationReturnList'],
    [PermissionCode.CaseSearch, '/case/inquery'],
  ]
  const reportCandidates: Array<[string, string]> = [
    [PermissionCode.MenuReportSalesAppropriation, '/report/sales-appropriation'],
    [PermissionCode.MenuReportSalesPerformance, '/report/sales-performance'],
    [PermissionCode.MenuReportDailyWebsiteCaseSummary, '/report/daily-website-case-summary'],
    [PermissionCode.MenuReportPerformance, '/report/performance'],
    [PermissionCode.MenuReportMarketing, '/report/temporary-marketing'],
    [PermissionCode.MenuReportDailyWebsiteTraffic, '/report/daily-website-traffic'],
    [PermissionCode.MenuReportDailyTrend, '/report/daily-case-trend'],
    [PermissionCode.MenuReportDailyPendingCaseTrend, '/report/daily-pending-case-trend'],
    [
      PermissionCode.MenuReportDailyCustomerSubmissionTrend,
      '/report/daily-customer-submission-trend',
    ],
    [PermissionCode.MenuReportDailySalesSubmissionTrend, '/report/daily-sales-submission-trend'],
    [PermissionCode.MenuReportDailyReviewSubmissionTrend, '/report/daily-review-submission-trend'],
    [PermissionCode.MenuReportRealtime, '/report/realtime-dashboard'],
  ]
  const adminCandidates: Array<[string, string]> = [
    [PermissionCode.MenuRosterManagement, '/admin/rosters'],
    [PermissionCode.MenuAdminUsers, '/admin/users'],
    [PermissionCode.MenuAdminPermissions, '/admin/permissions'],
    [PermissionCode.MenuAdminContactResultCodes, '/admin/contact-result-codes'],
    [PermissionCode.MenuAdminUat, '/admin/uatImport'],
  ]
  const downloadCandidates: Array<[string, string]> = [
    [PermissionCode.MenuDownloadCenter, '/downloads'],
  ]
  const reportFirst =
    user?.roleCode === RoleCode.HosinOwner ||
    user?.roleCode === RoleCode.HosinManager ||
    user?.roleCode === RoleCode.PartnerDistributor
  const candidates = reportFirst
    ? [...reportCandidates, ...caseCandidates, ...downloadCandidates, ...adminCandidates]
    : [...caseCandidates, ...reportCandidates, ...downloadCandidates, ...adminCandidates]
  const match = candidates.find(([permission]) => hasPermission(user, permission))
  if (match) return match[1]
  return '/login'
}

export function canAccessResolvedRoute(
  route: RouteLocationResolvedGeneric,
  user: UserInfo | null,
): boolean {
  const leaf = route.matched.at(-1)
  if (!leaf) return false

  const requiredPermissions =
    (leaf.meta.permissions as string[] | undefined) ??
    (leaf.meta.permission
      ? [leaf.meta.permission as string]
      : leaf.meta.systemRoles
        ? [PermissionCode.CaseList]
        : [])
  if (
    requiredPermissions.length > 0 &&
    !requiredPermissions.some((item) => hasPermission(user, item))
  ) {
    return false
  }

  const allowedRoleCodes = leaf.meta.roleCodes as string[] | undefined
  return !allowedRoleCodes || Boolean(user?.roleCode && allowedRoleCodes.includes(user.roleCode))
}
